import { useEffect, useState } from "react";

import { ApolloError } from "@apollo/client";
import { Key, Mail } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { buildUrl, getHash } from "@/components/helpers/url-utils";
import { validateEmail } from "@/components/helpers/validation/validate-email";
import {
  isCoach,
  isWorkspaceAgent,
} from "@/components/helpers/workspace/permissions";
import { PageTitle } from "@/components/pieces/onboarding/PageTitle";
import { setTokens } from "@/components/session/JwtTokenManager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SITE_NAME } from "@/css/constants";
import {
  LoginMutation,
  MiniWorkspace,
  useCheckEmailSsoWorkspaceLazyQuery,
  useLoginMutation,
  WorkspaceMemberRole,
} from "@/graphql";
import { DEFAULT_PERSONAL_WOKSPACE, LoginWorkspace } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { getWorkspaceHost, isSingleAccount } from "@/utils/workspaces";

import { WorkspaceSelector } from "./WorkspaceSelector";

function atBaseDomain() {
  const { host } = window.location;
  const isPublicHost = host.startsWith("www");
  const isLocalHost = host.startsWith("localhost");
  return isPublicHost || isLocalHost;
}

interface RedirectToWorkspaceProps {
  currentWorkspaceDomain?: string | null;
  currentWorkspaceRole?: WorkspaceMemberRole;
  sesameQuery: string;
  nextPath?: string | null;
  searchParams: URLSearchParams;
}

function redirectToWorkspace({
  currentWorkspaceDomain,
  currentWorkspaceRole,
  sesameQuery,
  nextPath,
  searchParams,
}: RedirectToWorkspaceProps) {
  let redirectApp = WEB_APP;
  if (currentWorkspaceDomain && !searchParams.get("noWorkspace")) {
    redirectApp = getWorkspaceHost(redirectApp, currentWorkspaceDomain);
  }

  let redirectPath = "/";
  if (nextPath) {
    if (nextPath.includes("?")) {
      redirectPath = nextPath + "&" + sesameQuery.slice(1);
    } else {
      redirectPath = nextPath + sesameQuery;
    }
  } else if (currentWorkspaceRole && isWorkspaceAgent(currentWorkspaceRole)) {
    redirectPath = "/home" + sesameQuery;
  } else if (isCoach()) {
    redirectPath = "/clients";
  } else {
    redirectPath = "/dashboard" + sesameQuery;
  }

  window.location.href = redirectApp + redirectPath;
}

interface LoginFormProps {
  nextPath?: string | null;
  onComplete?: (response: LoginMutation, skipSelectWorkspace?: boolean) => void;
  signUpLink?: string;
  inlineForm?: boolean;
}

export function LoginForm({
  nextPath,
  onComplete,
  signUpLink,
  inlineForm = false,
}: LoginFormProps) {
  const navigate = useNavigate();
  const workspaceSlug = useWorkspaceSlug();
  const [searchParams] = useSearchParams();

  let next = nextPath;
  if (!nextPath && searchParams.has("next")) {
    next = searchParams.get("next") || undefined;
  }

  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [loginFailed, setLoginFailed] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [loginResponsePayload, setLoginResponsePayload] =
    useState<LoginMutation>();

  // SSO-related state
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [isCheckingSSOWorkspace, setIsCheckingSSOWorkspace] = useState(false);
  const [isRedirectingToSSO, setIsRedirectingToSSO] = useState(false);

  // Add a local loading state to track the entire login process
  const [isNavigating, setIsNavigating] = useState(false);

  const [checkEmailSsoWorkspace] = useCheckEmailSsoWorkspaceLazyQuery();

  // Extract SSO checking logic into a separate function
  function handleSsoWorkspaceCheck() {
    if (!validateEmail(email)) return;

    setIsCheckingSSOWorkspace(true);
    checkEmailSsoWorkspace({ variables: { email } })
      .then(({ data }) => {
        if (
          data?.checkEmailSsoWorkspace?.isSsoWorkspace &&
          data.checkEmailSsoWorkspace.workspaceDomain
        ) {
          // Redirect to SSO
          setIsRedirectingToSSO(true);
          const ssoUrl = `${getWorkspaceHost(WEB_APP, data.checkEmailSsoWorkspace.workspaceDomain)}/sso`;
          window.location.href = ssoUrl;
        } else {
          // Show password field for regular login
          setShowPasswordField(true);
        }
      })
      .catch(() => {
        // On error, show password field as fallback
        setShowPasswordField(true);
      })
      .finally(() => {
        setIsCheckingSSOWorkspace(false);
      });
  }

  useEffect(() => {
    // Don't run SSO check if we're already on a workspace domain
    if (workspaceSlug) {
      setShowPasswordField(true);
      return;
    }

    if (!validateEmail(email)) {
      setShowPasswordField(false);
      return;
    }

    // Reset states when email changes, but don't automatically check SSO
    setShowPasswordField(false);
  }, [email, workspaceSlug]);

  function handleComplete(
    response: LoginMutation,
    skipSelectWorkspace = false,
  ) {
    if (!response.login) return;

    const { sesameQuery, currentWorkspaceDomain, currentWorkspaceRole, user } =
      response.login;

    if (!user) return;

    // Set navigating state as soon as we start the completion process
    setIsNavigating(true);

    const workspaces = ensureNonNullArray(response.login.workspaces);
    const hasIndividualAccount = user.hasIndividualAccount ?? true;

    // If there are multiple workspaces, we need to select one
    // before we can proceed, so we set the login response payload
    // which will trigger the WorkspaceSelector to render
    if (
      !skipSelectWorkspace &&
      !isSingleAccount(workspaces, hasIndividualAccount)
    ) {
      setLoginResponsePayload(response);
      setIsNavigating(false); // Stop navigating state since we're showing workspace selector
    } else if (onComplete) {
      setTokens({
        ...response.login,
        success: () => {
          onComplete(response);
        },
        error: handleError,
      });
    } else if (
      sesameQuery &&
      ((currentWorkspaceDomain && atBaseDomain()) ||
        !currentWorkspaceDomain ||
        !atBaseDomain())
    ) {
      redirectToWorkspace({
        currentWorkspaceDomain,
        currentWorkspaceRole: currentWorkspaceRole as WorkspaceMemberRole,
        sesameQuery,
        nextPath: next,
        searchParams,
      });
    } else {
      setTokens({
        ...response.login,
        success: () => {
          let path: string;

          if (
            currentWorkspaceRole &&
            isWorkspaceAgent(currentWorkspaceRole as WorkspaceMemberRole)
          ) {
            let redirectApp = WEB_APP;
            if (currentWorkspaceDomain && !searchParams.get("noWorkspace")) {
              redirectApp = getWorkspaceHost(
                redirectApp,
                currentWorkspaceDomain,
              );
            }
            path = "/home";
          } else if (isCoach()) {
            path = "/clients";
          } else {
            if (next && next !== "/app") {
              path = next;
            } else {
              path = "/dashboard";
            }
          }

          const appLink = buildUrl(path, {
            hash: getHash(),
          });

          if (window.location.href.endsWith(appLink)) {
            window.location.reload();
          } else {
            navigate(appLink, { replace: true });
          }
        },
        error: handleError,
      });
    }
  }

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      if (e.currentTarget.name === "email" && !showPasswordField) {
        // If user presses enter on email field and password isn't shown yet,
        // trigger the SSO check
        handleSsoWorkspaceCheck();
      } else if (showPasswordField) {
        login();
      }
    }
  }

  function handleError(error?: ApolloError) {
    if (!error) {
      setLoginFailed(true);
      setFailureMessage("Unexpected error");
      return;
    }

    const incorrectCreds = error.message.includes("enter valid credentials");
    const isUnexpectedError = error.message.includes("Unexpected error");
    let failureMessage = error.graphQLErrors?.[0]?.message;

    if (incorrectCreds) {
      failureMessage =
        "The e-mail address and/or password you specified are not correct";
    }

    if (isUnexpectedError || !failureMessage) {
      failureMessage =
        "Sorry, something went wrong on our server. We are investigating ASAP. Please refresh the page and try again.";
    }

    if (error.graphQLErrors) {
      const temporarilyLocked = error.graphQLErrors.find(
        // @ts-ignore
        ({ context }) => context && context.status === 429,
      );
      if (temporarilyLocked) {
        failureMessage = temporarilyLocked.message;
      }
    }

    setLoginFailed(true);
    setFailureMessage(failureMessage);
  }

  const [login, { loading }] = useLoginMutation({
    variables: {
      email,
      password,
      workspace: workspaceSlug || undefined,
    },
    onCompleted: (response) => {
      const newResponse = response;
      const { user } = newResponse?.login || {};
      const hasIndividualAccount = user?.hasIndividualAccount ?? false;
      const workspaces = ensureNonNullArray(newResponse?.login?.workspaces);
      const singleWorkspace =
        workspaces && workspaces.length === 1 && workspaces[0];

      if (
        isSingleAccount(workspaces, hasIndividualAccount) &&
        singleWorkspace &&
        newResponse.login &&
        !newResponse.login?.currentWorkspaceDomain
      ) {
        newResponse.login.currentWorkspaceDomain = singleWorkspace.domain;
        newResponse.login.currentWorkspaceRole = singleWorkspace.role;
      }

      return handleComplete(newResponse, false);
    },
    onError: handleError,
  });

  const resetLink = buildUrl("/forgot-password", {
    data: {
      email,
      next,
    },
    hash: getHash(),
  });

  const { user } = loginResponsePayload?.login || {};
  const hasIndividualAccount = user?.hasIndividualAccount || true;
  const workspaces = ensureNonNullArray(
    loginResponsePayload?.login?.workspaces,
  );

  // Only display the workspace selector if a user has more than 1 option, and is not trying to login to a workspace
  if (!isSingleAccount(workspaces, hasIndividualAccount)) {
    const onLogin = onComplete || handleComplete;
    const workspacesArray = ensureNonNullArray(workspaces);
    const items = workspacesArray.concat(
      hasIndividualAccount ? [DEFAULT_PERSONAL_WOKSPACE as MiniWorkspace] : [],
    );

    const onClick = (item: LoginWorkspace) => {
      const response = loginResponsePayload;
      if (!response || !response.login) return;

      response.login.currentWorkspaceDomain = item.domain;
      response.login.currentWorkspaceRole = item.role;

      onLogin(response, true);
    };

    return (
      <WorkspaceSelector
        items={items}
        onClick={onClick}
        inlineForm={inlineForm}
      />
    );
  }

  // Add this function to determine button text and state
  function getButtonProps() {
    if (isCheckingSSOWorkspace) {
      return {
        text: "Checking workspace...",
        loading: true,
        disabled: true,
        onClick: () => {},
      };
    }

    if (isRedirectingToSSO) {
      return {
        text: "Redirecting to SSO...",
        loading: true,
        disabled: true,
        onClick: () => {},
      };
    }

    if (!showPasswordField) {
      // Email entered but password not shown yet
      const isValidEmail = validateEmail(email);
      return {
        text: "Continue",
        loading: false,
        disabled: !isValidEmail,
        onClick: handleSsoWorkspaceCheck,
      };
    }

    // Password field is shown - regular login
    return {
      text: isNavigating ? "Logging in..." : "Login",
      loading: loading || isNavigating,
      disabled: !email || !password || loading || isNavigating,
      onClick: () => login(),
    };
  }

  const buttonProps = getButtonProps();

  return (
    <div className={`${inlineForm ? "w-full" : "sm:w-full"} max-w-md mx-auto`}>
      {!inlineForm && (
        <PageTitle
          title={`Login to ${SITE_NAME}`}
          style={{ textAlign: "center" }}
        />
      )}

      <Card
        className={`${inlineForm ? "border-0 shadow-none" : "border shadow-xs"}`}
      >
        <CardContent className="w-md">
          <div className="space-y-4">
            <div className="space-y-4">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@gmail.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setLoginFailed(false);
                  }}
                  disabled={loading}
                  onKeyDown={handleKeyPress}
                  autoFocus={!email}
                  className="pl-10 h-12 text-base"
                />
              </div>
            </div>

            {showPasswordField && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password..."
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setLoginFailed(false);
                    }}
                    disabled={loading}
                    onKeyDown={handleKeyPress}
                    autoFocus={!!email}
                    className="pl-10 h-12 text-base"
                  />
                </div>
              </div>
            )}

            {loginFailed && (
              <Alert className="bg-yellow-50 border-yellow-200 before:bg-yellow-600">
                <AlertDescription className="text-yellow-800">
                  {failureMessage}
                </AlertDescription>
              </Alert>
            )}

            <Button
              intent="primary"
              size="lg"
              className="w-full"
              disabled={buttonProps.disabled}
              onClick={buttonProps.onClick}
              {...(buttonProps.loading && { "aria-busy": true })}
            >
              {buttonProps.loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              )}
              {buttonProps.text}
            </Button>

            <div className="pt-4 text-center">
              <Link
                to={resetLink}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Forgot your password?
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {signUpLink && (
        <div className="mt-5 p-3 rounded-md text-center">
          <span className="text-sm text-gray-600">
            {`Don't have an account yet? `}
          </span>
          <Link
            to={signUpLink}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Sign up here
          </Link>
        </div>
      )}
    </div>
  );
}
